/**
 * **Die Checkliste, die aus einer Pflicht entstünde** — aus Pflichten/Ausschlüsse heraus, vor dem Aktivieren.
 *
 * Dieselbe Ableitung wie beim Aktivieren (tri-server `previewChecklistFor`): freigegebene Master-Checkliste
 * aus dem Recht, sonst freigegebene Checklisten-Definition, sonst ein Prüfpunkt aus dem Pflichttext.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../../State";
import { useLegalBindingAPI } from "../../LegalBindingAPI";
import { useModalContext } from "../../components/ModalProvider";
import { qualificationRoleName } from "../../administration/operator-responsibility/legalDisplay";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/** A cell value as text — an unexpected structure must never take the page down (15.10.2026). */
const asText = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

interface Properties {
  obligationKey: string | null;
  /** The subscription it is looked at from — its option decides whether checklists under review count. */
  bindingId?: string;
  onClose: () => void;
}

export default function ChecklistPreviewDialog({ obligationKey, bindingId, onClose }: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const legalAPI = useLegalBindingAPI(authentication?.customer);
  const [preview, setPreview] = useState<any>(null);

  useEffect(() => {
    if (!obligationKey) return;
    let cancelled = false;
    setPreview(null);
    legalAPI
      .getChecklistPreview(obligationKey, bindingId)
      .then((result: any) => !cancelled && setPreview(result))
      .catch((error: any) => {
        if (!cancelled) {
          pushError(error);
          onClose();
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obligationKey]);

  const o = preview?.obligation;
  return (
    <Dialog open={!!obligationKey} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>{preview?.master?.name ?? (preview?.unreleased ? preview?.pendingMaster?.name : null) ?? t("operatorObligationsPanel.checklistPreview.title")}</DialogTitle>
          <DialogDescription className="break-words">
            {o ? `${o.regulationName ?? o.regulationId} ${o.provisionId ?? ""} · ${obligationKey}` : obligationKey}
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="flex flex-col gap-2" data-testid="checklist-preview-loading">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="flex min-w-0 flex-col gap-3" data-testid="checklist-preview">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={preview.source === "master" ? "success" : "secondary"}>
                {t(`operatorObligationsPanel.checklistPreview.source.${preview.source}`)}
              </Badge>
              <Badge variant="outline">{t(`operatorObligationsPanel.checklistPreview.temporality.${o.temporality}`)}</Badge>
              {preview.master?.role ? <Badge variant="outline">{qualificationRoleName(t, preview.master.role)}</Badge> : null}
              {preview.master?.signatureRequired ? (
                <Badge variant="outline">{t("operatorObligationsPanel.checklistPreview.signature")}</Badge>
              ) : null}
              {preview.unreleased ? (
                <Badge variant="warning">{t("operatorObligationsPanel.checklistPreview.checklistUnreleased")}</Badge>
              ) : null}
              {o.reviewStatus !== "confirmed" && o.reviewStatus !== "legalConfirmed" ? (
                <Badge variant="warning">{t("operatorObligationsPanel.checklistPreview.unreleased")}</Badge>
              ) : null}
            </div>
            <p className="break-words text-sm text-muted-foreground">{o.description}</p>
            {preview.pendingMaster ? (
              <p className="text-xs text-muted-foreground">
                {t("operatorObligationsPanel.checklistPreview.pendingMaster", { name: preview.pendingMaster.name })}
              </p>
            ) : null}
            <div className="min-w-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>{t("operatorObligationsPanel.checklistPreview.task")}</TableHead>
                    <TableHead>{t("operatorObligationsPanel.checklistPreview.target")}</TableHead>
                    <TableHead>{t("operatorObligationsPanel.checklistPreview.interval")}</TableHead>
                    <TableHead>{t("operatorObligationsPanel.checklistPreview.reference")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.items.map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="tabular-nums">{index + 1}</TableCell>
                      <TableCell className="break-words">
                        {asText(item.task)}
                        {item.sourceText && item.sourceText !== item.task ? (
                          <div className="mt-0.5 text-xs italic text-muted-foreground">„{item.sourceText}“</div>
                        ) : null}
                        {item.optional ? <span className="text-xs text-muted-foreground"> · {t("operatorObligationsPanel.checklistPreview.optional")}</span> : null}
                      </TableCell>
                      <TableCell className="break-words">{asText(item.sollWert)}</TableCell>
                      <TableCell className="break-words">{asText(item.intervall)}</TableCell>
                      <TableCell className="break-words text-xs text-muted-foreground">{asText(item.normbezug)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
